import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, ShoppingCart, X } from 'lucide-react';
import SeoHead from './SeoHead';
import FAQSection from './FAQSection';
import {
  soundHirePackages,
  soundAddOns,
  smokeMachinePackages,
} from '../data/ubuntuServices';

const WHATSAPP_NUMBER = '27663286231';
const STORAGE_KEY = 'ubuntu-events-management-sound-selection';
const faqItems = [
  { q: 'Do you offer sound hire in Pretoria?', a: 'Yes, we provide sound hire in Pretoria and nearby Gauteng areas including Centurion and Midrand.' },
  { q: 'Can I add microphones and smoke effects?', a: 'Yes, you can add sound extras and smoke machine packages to your quote.' },
  { q: 'Is this suitable for indoor and outdoor events?', a: 'Yes, we provide packages suited to both indoor and outdoor setups.' },
  { q: 'How do I get pricing near me?', a: 'Select your package and send the generated WhatsApp quote for fast confirmation.' },
  { q: 'How early should I book?', a: 'Weekend bookings fill fast, so early booking is recommended.' },
];

const openWhatsApp = (message) => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    message
  )}`;
  window.open(whatsappUrl, '_blank');
};

const SoundHire = () => {
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);
  const [selectedSmokePackageId, setSelectedSmokePackageId] = useState('');
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      setSelectedPackageId(parsed.selectedPackageId || '');
      setSelectedAddOnIds(parsed.selectedAddOnIds || []);
      setSelectedSmokePackageId(parsed.selectedSmokePackageId || '');
    } catch (_error) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        selectedPackageId,
        selectedAddOnIds,
        selectedSmokePackageId,
      })
    );
  }, [selectedPackageId, selectedAddOnIds, selectedSmokePackageId]);

  const selectedPackage = useMemo(
    () => soundHirePackages.find((pkg) => pkg.id === selectedPackageId) || null,
    [selectedPackageId]
  );
  const selectedSmokePackage = useMemo(
    () =>
      smokeMachinePackages.find((pkg) => pkg.id === selectedSmokePackageId) ||
      null,
    [selectedSmokePackageId]
  );
  const selectedAddOns = useMemo(
    () => soundAddOns.filter((addOn) => selectedAddOnIds.includes(addOn.id)),
    [selectedAddOnIds]
  );

  const hasSelection =
    Boolean(selectedPackageId) ||
    selectedAddOnIds.length > 0 ||
    Boolean(selectedSmokePackageId);

  const estimatedTotal = useMemo(() => {
    const packagePrice = selectedPackage?.price || 0;
    const smokePrice = selectedSmokePackage?.price || 0;
    const addOnsPrice = selectedAddOns.reduce(
      (sum, addOn) => sum + (addOn.price || 0),
      0
    );
    return packagePrice + smokePrice + addOnsPrice;
  }, [selectedPackage, selectedSmokePackage, selectedAddOns]);

  const toggleAddOn = (addOnId) => {
    setSelectedAddOnIds((prev) =>
      prev.includes(addOnId)
        ? prev.filter((id) => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  const buildWhatsAppListMessage = () => {
    const lines = [];
    if (selectedPackage) {
      lines.push(
        `1. Sound Package: ${selectedPackage.name}\n   ${
          selectedPackage.priceLabel || `R${selectedPackage.price.toLocaleString()}`
        }`
      );
    }
    selectedAddOns.forEach((addOn, idx) => {
      lines.push(
        `${idx + 2}. Add-on: ${addOn.name}\n   ${
          addOn.priceLabel || `R${addOn.price.toLocaleString()}`
        }`
      );
    });
    if (selectedSmokePackage) {
      lines.push(
        `${lines.length + 1}. Smoke Package: ${selectedSmokePackage.name}\n   ${
          selectedSmokePackage.priceLabel ||
          `R${selectedSmokePackage.price.toLocaleString()}`
        }`
      );
    }

    return `Hi UBUNTU EVENTS MANAGEMENT,

I'd like a quote for the following sound hire selection:

${lines.join('\n\n')}

Estimated total: R${estimatedTotal.toLocaleString()}

Please confirm availability and final pricing for my event date.
Thank you!`;
  };

  const sendWhatsAppMessage = () => {
    if (!selectedPackage) {
      window.alert(
        'Please select one Sound Hire package before sending your request.'
      );
      return;
    }
    const message = buildWhatsAppListMessage();
    openWhatsApp(message);
  };

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-cream to-white py-12 px-4 ${
        hasSelection ? 'pb-32 sm:pb-28' : ''
      }`}
    >
      <SeoHead
        title="Sound Hire Pretoria | Ubuntu Enterprises"
        description="Professional sound hire in Pretoria and Gauteng with package options, add-ons, and fast WhatsApp quotes."
        canonicalPath="/soundhire"
        faqSchema={faqItems}
      />
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] flex items-center justify-center overflow-hidden rounded-2xl">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url('https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80')",
          }}
        />
        <div className="hero-overlay" />
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-playfair font-bold mb-6"
          >
            Professional Sound Hire for Indoor &amp; Outdoor Events
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-2xl font-light mb-10 text-white/90"
          >
            Clear sound, smooth setup, and reliable event support for
            celebrations, functions, and corporate experiences.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => setShowCart(true)}
              className="btn-primary text-lg px-8 py-4"
            >
              Review Selection
            </button>
          </motion.div>
        </div>
      </section>

      {/* Packages */}
      <section className="mt-14">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
              Sound Hire Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Premium sound setups tailored for speeches, music, and every
              moment that needs to be heard clearly.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {soundHirePackages.map((pkg, idx) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.06 }}
                viewport={{ once: true }}
                className="card group hover:shadow-2xl flex flex-col"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-xl mb-5">
                  <img
                    src={pkg.image}
                    alt={`${pkg.name} sound hire in Gauteng`}
                    className="w-full h-40 sm:h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="flex-1">
                  <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-2">
                    {pkg.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>

                  <ul className="text-sm text-gray-700 space-y-2 mb-5">
                    {pkg.features.map((feature, i) => (
                      <li key={`${pkg.id}-f-${i}`} className="flex items-center">
                        <span className="text-gold mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-baseline justify-between mb-5">
                    <span className="text-3xl font-bold text-gold">
                      {pkg.priceLabel || `R${pkg.price.toLocaleString()}`}
                    </span>
                    <span className="text-sm text-gray-500">per day</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setSelectedPackageId(pkg.id)}
                    className={`w-full text-sm py-3 rounded-lg font-semibold transition ${
                      selectedPackageId === pkg.id
                        ? 'bg-gold text-white'
                        : 'btn-primary'
                    }`}
                  >
                    {selectedPackageId === pkg.id
                      ? 'Selected Package'
                      : 'Select Package'}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="mt-16">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
              Sound Add-ons
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Upgrade your setup with extra microphones, technician support,
              lighting accents, and optional smoke effects.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {soundAddOns.map((addOn, idx) => (
              <motion.div
                key={addOn.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.06 }}
                viewport={{ once: true }}
                className="card group hover:shadow-2xl flex flex-col"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-xl mb-5">
                  <img
                    src={addOn.image}
                    alt={`${addOn.name} event sound add-on Gauteng`}
                    className="w-full h-40 sm:h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="flex-1">
                  <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-2">
                    {addOn.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-5">{addOn.description}</p>

                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-3xl font-bold text-gold">
                    {addOn.priceLabel || `R${addOn.price.toLocaleString()}`}
                    </span>
                    <span className="text-sm text-gray-500">add-on</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => toggleAddOn(addOn.id)}
                    className={`w-full text-sm py-3 rounded-lg font-semibold transition ${
                      selectedAddOnIds.includes(addOn.id)
                        ? 'bg-olive text-white'
                        : 'btn-secondary'
                    }`}
                  >
                    {selectedAddOnIds.includes(addOn.id)
                      ? 'Added'
                      : 'Add This Add-on'}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Smoke Machine Hire */}
      <section className="mt-16">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
              Smoke Machine Hire
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Create an unforgettable entrance, elevate the stage, and add a
              premium atmosphere to your celebration.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {smokeMachinePackages.map((pkg, idx) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.06 }}
                viewport={{ once: true }}
                className="card group hover:shadow-2xl flex flex-col"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-xl mb-5">
                  <img
                    src={pkg.image}
                    alt={`${pkg.name} smoke machine hire Gauteng`}
                    className="w-full h-40 sm:h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="flex-1">
                  <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-2">
                    {pkg.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>

                  <div className="mb-5">
                    <span className="text-3xl font-bold text-gold">
                      R{pkg.price.toLocaleString()}
                    </span>{' '}
                    <span className="text-sm text-gray-500">per day</span>
                  </div>

                  {pkg.features?.length ? (
                    <ul className="text-sm text-gray-700 space-y-2">
                      {pkg.features.map((f, i) => (
                        <li key={`${pkg.id}-feat-${i}`} className="flex items-center">
                          <span className="text-gold mr-2">✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedSmokePackageId((prev) =>
                        prev === pkg.id ? '' : pkg.id
                      )
                    }
                    className={`w-full text-sm py-3 rounded-lg font-semibold transition ${
                      selectedSmokePackageId === pkg.id
                        ? 'bg-olive text-white'
                        : 'btn-secondary'
                    }`}
                  >
                    {selectedSmokePackageId === pkg.id
                      ? 'Added'
                      : 'Add Smoke Package'}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-8 text-center text-sm text-gray-600 italic">
            Smoke effects are subject to venue rules and ventilation
            requirements.
          </p>
        </div>
      </section>

      {hasSelection && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-black/15 bg-[#f5f0e6]/95 backdrop-blur-sm shadow-[0_-4px_24px_rgba(0,0,0,0.08)] pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowCart(true)}
              className="flex items-center gap-3 text-left text-black min-w-0 flex-1"
              aria-label="Review selections"
            >
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2a2a2a] text-[#f5f0e6]">
                <ShoppingCart size={20} />
                <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                  {Number(Boolean(selectedPackageId)) +
                    selectedAddOnIds.length +
                    Number(Boolean(selectedSmokePackageId))}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-black">
                  Sound selection · R{estimatedTotal.toLocaleString()}
                </span>
                <span className="block text-xs text-black/55 truncate">
                  Tap to review and send your selected package + add-ons
                </span>
              </span>
            </button>
            <div className="flex gap-2 shrink-0 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowCart(true)}
                className="flex-1 sm:flex-none rounded-lg border border-black/20 bg-white/60 px-4 py-2.5 text-sm font-semibold text-black hover:bg-white/90"
              >
                Review
              </button>
              <button
                type="button"
                onClick={sendWhatsAppMessage}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
              >
                <MessageCircle size={18} />
                WhatsApp
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {showCart && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#f5f0e6] rounded-t-2xl sm:rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-black/10 shadow-xl"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-black">
                Your sound selection
              </h3>
              <button
                type="button"
                onClick={() => setShowCart(false)}
                className="text-black/50 hover:text-black"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            {!hasSelection ? (
              <p className="text-sm text-black/70">
                No items selected yet.
              </p>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  <div className="border-b border-black/10 pb-2">
                    <p className="text-xs text-black/60 mb-1">Selected package</p>
                    <p className="font-semibold text-sm text-black">
                      {selectedPackage?.name || 'None selected'}
                    </p>
                    {selectedPackage ? (
                      <p className="text-xs text-black/60">
                        {selectedPackage.priceLabel ||
                          `R${selectedPackage.price.toLocaleString()}`}
                      </p>
                    ) : null}
                  </div>

                  <div className="border-b border-black/10 pb-2">
                    <p className="text-xs text-black/60 mb-1">Add-ons</p>
                    {selectedAddOns.length === 0 ? (
                      <p className="text-sm text-black">No add-ons selected</p>
                    ) : (
                      <div className="space-y-1">
                        {selectedAddOns.map((addOn) => (
                          <p key={addOn.id} className="text-sm text-black">
                            {addOn.name} ({addOn.priceLabel || `R${addOn.price.toLocaleString()}`})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border-b border-black/10 pb-2">
                    <p className="text-xs text-black/60 mb-1">Smoke package</p>
                    <p className="text-sm text-black">
                      {selectedSmokePackage?.name || 'None selected'}
                    </p>
                    {selectedSmokePackage ? (
                      <p className="text-xs text-black/60">
                        {selectedSmokePackage.priceLabel ||
                          `R${selectedSmokePackage.price.toLocaleString()}`}
                      </p>
                    ) : null}
                  </div>
                </div>

                <p className="text-xs text-black/55 mb-3">
                  The list below is sent to WhatsApp exactly as shown.
                </p>
                <div className="rounded-lg border border-black/10 bg-white/40 p-3 mb-4 max-h-40 overflow-y-auto text-xs text-black/80 whitespace-pre-wrap font-mono leading-relaxed">
                  {buildWhatsAppListMessage()}
                </div>

                <div className="border-t border-black/15 pt-4 mb-4">
                  <div className="flex justify-between text-lg font-bold text-black">
                    <span>Estimated total:</span>
                    <span>R{estimatedTotal.toLocaleString()}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={sendWhatsAppMessage}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center space-x-2"
                >
                  <MessageCircle size={20} />
                  <span>Send full list to WhatsApp</span>
                </button>
              </>
            )}
          </motion.div>
        </div>
      )}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-7xl mx-auto mt-10 rounded-xl border border-gold/20 bg-white p-8"
      >
        <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-3">
          Our Other Services
        </h3>
        <p className="text-gray-600 mb-6">
          Build a full event package with catering, decor styling, and easy access to the homepage.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'menu' }))}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5"
          >
            Professional Event Catering
          </button>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'decor' }))}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5"
          >
            Elegant Event Decor Solutions
          </button>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'home' }))}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5 sm:col-span-2"
          >
            Back to Homepage
          </button>
        </div>
      </motion.section>
      <FAQSection title="Sound Hire FAQs" faqs={faqItems} />
    </div>
  );
};

export default SoundHire;

